import {
  Info as BuildInfoIcon,
  Close as CloseMenuIcon,
  OfflineBolt as OfflineIcon,
  Menu as OpenMenuIcon,
  ExitToApp as SignOutIcon,
  Refresh as UpdateAppIcon,
} from '@mui/icons-material';
import {
  Alert,
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { Suspense, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { MiddleSpinner } from './components/middle-spinner/index.js';
import { OrganizationSwitcher } from './components/organization-switcher/index.js';
import { CenterLayout } from './components/page-layout/index.js';
import { SiteTitle } from './components/title/index.js';
import useOnline from './hooks/use-online.js';
import { useUpdate } from './hooks/use-update.js';
import { MenuItems } from './menu-items.js';
import { useAuth } from './utils/auth/index.js';

const drawerWidth = 240;

interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window;
}

const parsedBuildDate = DateTime.fromISO(import.meta.env.VITE_APP_BUILD ?? '');
const buildDate = parsedBuildDate.isValid
  ? parsedBuildDate.toLocaleString(DateTime.DATE_MED)
  : 'DEV';
const buildTime = parsedBuildDate.isValid
  ? parsedBuildDate.toLocaleString(DateTime.TIME_SIMPLE)
  : 'manually';

export const Layout: FC<Props> = (props) => {
  const auth = useAuth();
  const { window } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { reloadPage, updateAvailable } = useUpdate();
  const isOnline = useOnline();

  const handleDrawerToggle = (): void => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <>
      <List sx={{ flexGrow: 1 }}>
        <MenuItems />
        <ListItem disablePadding>
          <ListItemButton onClick={() => void auth.signOut()}>
            <ListItemIcon>
              <SignOutIcon />
            </ListItemIcon>
            <ListItemText primary="Sign Out" />
          </ListItemButton>
        </ListItem>
      </List>
      <List>
        {updateAvailable && (
          <>
            <Divider />
            <ListItem disablePadding>
              <ListItemButton onClick={() => reloadPage()}>
                <ListItemIcon>
                  <UpdateAppIcon />
                </ListItemIcon>
                <ListItemText primary="Update app" />
              </ListItemButton>
            </ListItem>
          </>
        )}
        <ListItem>
          <ListItemIcon>
            <BuildInfoIcon />
          </ListItemIcon>
          <ListItemText primary={buildDate} secondary={`built ${buildTime}`} />
        </ListItem>
      </List>
    </>
  );

  const container = (): Element | null =>
    window ? window()?.document.body : null;

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      <AppBar
        color="secondary"
        enableColorOnDark
        position="fixed"
        sx={{ zIndex: 10000 }}
      >
        <Toolbar>
          {smallScreen ? (
            <>
              <IconButton
                aria-label="open drawer"
                color="inherit"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                {mobileOpen ? <CloseMenuIcon /> : <OpenMenuIcon />}
              </IconButton>
              <SiteTitle />
            </>
          ) : (
            <Box sx={{ ml: '240px', width: '100%' }}>
              <SiteTitle />
            </Box>
          )}
          <Box sx={{ flexShrink: 0, ml: 'auto', pl: 1 }}>
            <OrganizationSwitcher />
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        aria-label="mailbox folders"
        component="nav"
        sx={{
          display: 'flex',
          flexShrink: { sm: 0 },
          width: { sm: drawerWidth },
        }}
      >
        {smallScreen ? (
          <Drawer
            container={container}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            onClick={handleDrawerToggle}
            onClose={handleDrawerToggle}
            open={mobileOpen}
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
              },
              display: { sm: 'none', xs: 'block' },
            }}
            variant="temporary"
          >
            <Toolbar />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                overflow: 'auto',
              }}
            >
              {drawer}
            </Box>
          </Drawer>
        ) : (
          <Drawer
            open
            PaperProps={{ elevation: 4 }}
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
              },
              display: { sm: 'block', xs: 'none' },
            }}
            variant="permanent"
          >
            <Toolbar />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                overflow: 'auto',
              }}
            >
              {drawer}
            </Box>
          </Drawer>
        )}
      </Box>
      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          overflowX: 'auto',
          p: { md: 3, xs: 1 },
        }}
      >
        <Toolbar />
        {!isOnline && (
          <CenterLayout>
            <Alert
              color="warning"
              icon={<OfflineIcon />}
              sx={{ mb: { md: 3, xs: 1 } }}
              variant="filled"
            >
              This device is currently offline. Please check your internet
              connection.
            </Alert>
          </CenterLayout>
        )}
        {updateAvailable && (
          <CenterLayout>
            <Alert
              icon={<UpdateAppIcon />}
              onClick={() => reloadPage()}
              sx={{ cursor: 'pointer', mb: { md: 3, xs: 1 } }}
            >
              Update available. Click here to install now.
            </Alert>
          </CenterLayout>
        )}
        <Suspense fallback={<MiddleSpinner />}>
          <Outlet />
        </Suspense>
      </Box>
    </Box>
  );
};
